import type { SummaryState } from '@/components/shared/link-preview/types';
import {
  DEFAULT_SUMMARY_LENGTH_INDEX,
  getModelLabel,
  getSummaryLengthLabel,
  getSummaryLengthOptionByIndex,
  isModelOption,
  isSummaryLengthOption,
  type ModelOption,
  SUMMARY_LENGTH_OPTIONS,
} from '@/lib/link-preview/summary-options';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import { formatTranscriptSource } from './formatters';
import type { SummaryDisplay } from './types';

export function buildSummaryDisplay({
  summaryState,
  summaryLengthIndex,
  selectedModel,
  isSummarizing,
}: {
  summaryState: SummaryState;
  summaryLengthIndex: number;
  selectedModel: ModelOption;
  isSummarizing: boolean;
}): SummaryDisplay {
  const summaryHasContent = Boolean(toTrimmedNonEmptyString(summaryState.text));
  const shouldRenderSummaryCard = summaryState.status !== 'idle' || summaryHasContent;
  const summarizeButtonLabel = isSummarizing ? 'Summarizing…' : 'Summarize';

  const configuredSummaryOption =
    getSummaryLengthOptionByIndex(summaryLengthIndex) ??
    getSummaryLengthOptionByIndex(DEFAULT_SUMMARY_LENGTH_INDEX) ??
    SUMMARY_LENGTH_OPTIONS[0];
  const configuredSummaryLengthValue = configuredSummaryOption.value;
  const configuredSummaryLengthLabel =
    getSummaryLengthLabel(configuredSummaryLengthValue) ?? getSummaryLengthLabel('medium') ?? 'Medium';
  const configuredModelLabel = getModelLabel(selectedModel);

  const summaryMetadata = summaryState.metadata;
  const summaryLengthForDisplay = isSummaryLengthOption(summaryMetadata.summaryLength)
    ? summaryMetadata.summaryLength
    : null;
  const resolvedSummaryLengthLabel = getSummaryLengthLabel(summaryLengthForDisplay) ?? configuredSummaryLengthLabel;
  const resolvedModelLabel = (() => {
    const model = summaryMetadata.model;
    if (!model) {
      return configuredModelLabel;
    }
    if (typeof model === 'string' && isModelOption(model)) {
      return getModelLabel(model);
    }
    return model;
  })();

  const summaryCharactersDisplay =
    typeof summaryMetadata.summaryCharacters === 'number' ? summaryMetadata.summaryCharacters.toLocaleString() : '—';
  const summaryWordCountDisplay =
    typeof summaryMetadata.wordCount === 'number' ? summaryMetadata.wordCount.toLocaleString() : '—';

  const transcriptDisplay = (() => {
    if (summaryMetadata.transcriptAvailable === false) {
      return summaryMetadata.transcriptMessage ?? 'Transcript unavailable (HTML fallback).';
    }
    if (typeof summaryMetadata.transcriptCharacters === 'number') {
      const chars = summaryMetadata.transcriptCharacters.toLocaleString();
      const lines =
        typeof summaryMetadata.transcriptLines === 'number'
          ? ` · ${summaryMetadata.transcriptLines.toLocaleString()} lines`
          : '';
      const sourceLabel = formatTranscriptSource(summaryMetadata.transcriptSource ?? null);
      return `${chars} characters${lines}${sourceLabel}`;
    }
    return '—';
  })();

  const summaryTruncated = summaryMetadata.truncated ?? null;
  const contentTruncated = summaryMetadata.contentTruncated ?? null;
  let summaryTruncatedDisplay = '—';
  if (summaryTruncated !== undefined) {
    summaryTruncatedDisplay = summaryTruncated ? 'Yes' : 'No';
  }

  return {
    summaryHasContent,
    shouldRenderSummaryCard,
    summarizeButtonLabel,
    resolvedSummaryLengthLabel,
    resolvedModelLabel,
    summaryCharactersDisplay,
    summaryWordCountDisplay,
    transcriptDisplay,
    summaryTruncatedDisplay,
    summaryTruncated,
    contentTruncated,
  };
}
